import React from "react";
import { fireEvent, render, screen, waitFor } from "@testing-library/react";
import "@testing-library/jest-dom";
import {
  buildToolResultImages,
  getToolResultConversationId,
  FullPageChatLayout,
  ToolResultImage,
} from "./FullPageChatLayout";

let mockDocumentJobs: any[] = [];

const mockAuthFetch = jest.fn();
const mockDownloadDocument = jest.fn();
const mockResolveDocumentDownloadLink = jest.fn();
const mockCreateObjectURL = jest.fn(() => "blob:rendered-image");
const mockRevokeObjectURL = jest.fn();

beforeAll(() => {
  Object.defineProperty(HTMLElement.prototype, "scrollIntoView", {
    configurable: true,
    value: jest.fn(),
  });
  Object.defineProperty(URL, "createObjectURL", {
    configurable: true,
    value: mockCreateObjectURL,
  });
  Object.defineProperty(URL, "revokeObjectURL", {
    configurable: true,
    value: mockRevokeObjectURL,
  });
});

jest.mock("../services/api", () => ({
  useDownloadDocument: () => mockDownloadDocument,
  useResolveDocumentDownloadLink: () => mockResolveDocumentDownloadLink,
}));

jest.mock("../../../utils/useAuthFetch", () => ({
  useAuthFetch: () => mockAuthFetch,
}));

jest.mock("../../../providers/WorkspaceProvider", () => ({
  useWorkspaceContext: () => ({ workspace: { id: "workspace-1" } }),
}));

jest.mock("@metalmind-ab/mmagent-chat-ui", () => {
  const ReactActual = jest.requireActual<typeof import("react")>("react");
  return {
    ChatMessage: ({ message, toolExtensions }: any) =>
      ReactActual.createElement(
        ReactActual.Fragment,
        null,
        ...(message.toolExecutions ?? []).map((execution: any) =>
          ReactActual.createElement(
            ReactActual.Fragment,
            { key: execution.id },
            toolExtensions?.renderResult?.(execution),
          ),
        ),
      ),
    JobProgressIndicator: () => null,
    useChatStore: (selector: (state: unknown) => unknown) =>
      selector({
        backgroundJobs: mockDocumentJobs,
        removeBackgroundJob: jest.fn(),
        messages: [],
      }),
  };
});

const execution = {
  id: "tool-1",
  name: "verify_artifact_render",
  args: {},
  status: "completed" as const,
  result: {
    success: true,
    result: { artifact: { filename: "dashboard.html" }, rendered: [1, 2] },
    image_refs: [
      {
        attachment_id: "attachment-1",
        media_type: "image/jpeg",
        size_bytes: 12,
      },
      {
        attachment_id: "attachment-2",
        media_type: "image/jpeg",
        size_bytes: 13,
      },
    ],
  },
};

describe("verify_artifact_render tool-result images", () => {
  afterEach(() => {
    jest.clearAllMocks();
    jest.restoreAllMocks();
  });

  it("renders each returned image reference through the conversation image route", async () => {
    mockAuthFetch.mockResolvedValue({ blob: async () => new Blob(["image"]) });
    const result = buildToolResultImages(execution, 42);
    render(<>{result}</>);

    expect(await screen.findAllByRole("img")).toHaveLength(2);
    await waitFor(() =>
      expect(mockAuthFetch).toHaveBeenCalledWith(
        "/api/workspace/ida/chat/conversations/42/images/attachment-1",
        "load rendered artifact image",
        { headers: { "X-Workspace-Id": "workspace-1" } },
        30000,
        { suppressNetworkBanner: true },
      ),
    );
    expect(mockAuthFetch).toHaveBeenCalledWith(
      "/api/workspace/ida/chat/conversations/42/images/attachment-2",
      "load rendered artifact image",
      { headers: { "X-Workspace-Id": "workspace-1" } },
      30000,
      { suppressNetworkBanner: true },
    );
  });

  it("loads the protected image with the workspace header and shows expiry only after a load failure", async () => {
    const blobUrl = "blob:rendered-image";
    mockCreateObjectURL.mockReturnValue(blobUrl);
    mockAuthFetch.mockResolvedValue({ blob: async () => new Blob(["image"]) });

    render(
      <ToolResultImage
        src="/api/workspace/ida/chat/conversations/42/images/attachment-1"
        alt="dashboard.html page 1"
      />,
    );

    await waitFor(() =>
      expect(mockAuthFetch).toHaveBeenCalledWith(
        "/api/workspace/ida/chat/conversations/42/images/attachment-1",
        "load rendered artifact image",
        { headers: { "X-Workspace-Id": "workspace-1" } },
        30000,
        { suppressNetworkBanner: true },
      ),
    );
    await waitFor(() =>
      expect(
        screen.getByRole("img", { name: "dashboard.html page 1" }),
      ).toHaveAttribute("src", blobUrl),
    );
    expect(screen.queryByText("Image expired")).not.toBeInTheDocument();

    fireEvent.error(screen.getByRole("img", { name: "dashboard.html page 1" }));
    expect(await screen.findByText("Image expired")).toBeInTheDocument();
  });

  it("renders image refs returned by other tools and skips them without an active conversation", () => {
    expect(buildToolResultImages(execution, null)).toBeNull();
    expect(
      buildToolResultImages({ ...execution, name: "other_tool" }, 42),
    ).not.toBeNull();
  });

  it("keeps tool images owned by the visible history while a new selection loads", async () => {
    // Chat selects the new id before its history request finishes. Until the
    // messages are replaced, their tool results still belong to conversation 42.
    const imageConversationId = getToolResultConversationId(43, 42);
    mockAuthFetch.mockResolvedValue({ blob: async () => new Blob(["image"]) });
    render(<>{buildToolResultImages(execution, imageConversationId)}</>);

    expect(getToolResultConversationId(43, 42)).toBe(42);
    expect(getToolResultConversationId(43, null)).toBe(43);
    await waitFor(() =>
      expect(mockAuthFetch).toHaveBeenCalledWith(
        "/api/workspace/ida/chat/conversations/42/images/attachment-1",
        "load rendered artifact image",
        { headers: { "X-Workspace-Id": "workspace-1" } },
        30000,
        { suppressNetworkBanner: true },
      ),
    );
  });
});

describe("background document cards", () => {
  beforeEach(() => {
    mockDocumentJobs = [];
  });

  it.each(["generate_document", "generate_presentation"])(
    "uses persisted identity when the live %s job has empty identity fields",
    (skillName) => {
      mockDocumentJobs = [
        {
          jobId: "job-1",
          skillName,
          status: "completed",
          result: { document_id: "", document_title: "   " },
        },
      ];

      render(
        <FullPageChatLayout
          messages={[
            {
              id: "assistant-1",
              role: "assistant",
              content: "",
              createdAt: new Date("2026-10-30T10:00:00Z"),
              toolExecutions: [
                {
                  id: "call-1",
                  name: skillName,
                  args: {},
                  status: "completed",
                  result: {
                    background: true,
                    job_id: "job-1",
                    document_id: "persisted-document-1",
                    document_title: "Persisted document.docx",
                  },
                },
              ],
            } as any,
          ]}
          onSendMessage={jest.fn()}
          isLoading={false}
          conversations={[]}
          onLoadConversation={jest.fn()}
          onNewConversation={jest.fn()}
          onSearchConversations={jest.fn()}
          onLoadMoreConversations={jest.fn()}
          conversationsLoading={false}
          currentConversationId={42}
          welcomeTitle="Welcome"
          welcomeDescription=""
          workspaceId="workspace-1"
        />,
      );

      expect(
        screen.getByRole("link", { name: "Persisted document.docx" }),
      ).toBeInTheDocument();
    },
  );
});
